import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  IconButton,
  Collapse,
  TextField,
  Button,
  CircularProgress,
  useTheme,
  Grid,
  Paper,
  ClickAwayListener,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import CardTravelIcon from "@mui/icons-material/CardTravel";
import NoFoodIcon from '@mui/icons-material/NoFood';
import ReplayIcon from '@mui/icons-material/Replay';
import ConnectingAirportsIcon from '@mui/icons-material/ConnectingAirports';
import HelpIcon from '@mui/icons-material/Help';
import DeckIcon from '@mui/icons-material/Deck';
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { 
  missionReportThunk,
  saveDeliveryFeedback,
  clearDeliveryFeedback,
  selectDeliveryFeedback
} from "../features/mission/missionSlice";
import { FlightStatus } from "../features/aircraft/types";

const ISSUE_DEFINITIONS = {
  camera_issue: {
    id: 'camera_issue',
    value: 'Camera Issue',
    icon: VideocamOffIcon
  },
  payload_issue: {
    id: 'payload_issue',
    value: 'Payload Issue',
    icon: CardTravelIcon
  },
  rtk_issue: {
    id: 'rtk_issue',
    value: 'RTK Issue',
    icon: SatelliteAltIcon
  },
  obstacle: {
    id: 'obstacle',
    value: 'Obstacle',
    icon: DeckIcon
  },
  incorrect_order: {
    id: 'incorrect_order',
    value: 'Incorrect Order',
    icon: NoFoodIcon
  },
  auto_abort: {
    id: 'auto_abort',
    value: 'Auto Abort',
    icon: ReplayIcon
  },
  airspace_incursion: {
    id: 'airspace_incursion',
    value: 'Airspace Incursion',
    icon: ConnectingAirportsIcon
  },
  other_issue: {
    id: 'other_issue',
    value: 'Other Issue',
    icon: HelpIcon
  }
};

export const ReviewFlight = (props: {
  uavId: string;
  missionId: string;
  flightPhase?: FlightStatus;
}) => {
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState<string>("");
  const [feedbackType, setFeedbackType] = useState<string>("");
  const [otherFeedback, setOtherFeedback] = useState<string>("");
  const [feedbackSeverity, setFeedbackSeverity] = useState<"NO_ISSUE" | "MINOR_ISSUE" | "MAJOR_ISSUE">("NO_ISSUE");
  const [missionQueue, setMissionQueue] = useState<string[]>([]);
  const [completedMissionQueue, setCompletedMissionQueue] = useState<string[]>(
    []
  );
  const [feedbackRestored, setFeedbackRestored] = useState<boolean>(false);

  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>();
  const takeoffProcessedRef = useRef<boolean>(false);

  const dispatch = useAppDispatch();
  const savedFeedback = useAppSelector(state => selectDeliveryFeedback(state, props.uavId));

  const completeMissionId = (missionId: string) => {
    const combinedIdToRemove = missionQueue.find((combinedId) => {
      const [missionPart, _] = combinedId.split(":");
      return missionPart === missionId;
    });

    if (combinedIdToRemove) {
      setMissionQueue((prevQueue) =>
        prevQueue.filter((id) => id !== combinedIdToRemove)
      );
      setCompletedMissionQueue((prevCompleted) => [
        ...prevCompleted,
        combinedIdToRemove,
      ]);
    }

    setShowFeedback(false);
    setFeedbackText("");
    setFeedbackType("");
    setOtherFeedback("");
    setFeedbackSeverity("NO_ISSUE");
    setFeedbackRestored(false);
  };

  const getNextMissionId = () => {
    const nextCombinedId = missionQueue
      .slice()
      .reverse()
      .find((combinedId) => !completedMissionQueue.includes(combinedId));

    if (nextCombinedId) {
      const [missionPart, _] = nextCombinedId.split(":");
      return missionPart;
    }

    return null;
  };

  const missionId = getNextMissionId();

  useEffect(() => {
    if (props.missionId && props.uavId) {
      const combinedId = `${props.missionId}:${props.uavId}`;

      if (!missionQueue.includes(combinedId)) {
        setMissionQueue((prevQueue) => [...prevQueue, combinedId]);
      }
    }
  }, [props.missionId, props.uavId, missionQueue]);

  useEffect(() => {
    if (inputRef.current && showFeedback) {
      inputRef.current.focus();
    }
  }, [showFeedback, inputRef]);

  useEffect(() => {
    if (props.flightPhase === "climbDelivery") {
      setIsSubmittingPositiveFeedback(false);
    }
  }, [props.flightPhase]);

  useEffect(() => {
    setShowFeedback(false);
    setFeedbackText("");
    setFeedbackType("");
    setOtherFeedback("");
    setFeedbackSeverity("NO_ISSUE");

    if (!savedFeedback) {
      setFeedbackRestored(false);
    }
  }, [missionId, props.uavId, savedFeedback]);

  useEffect(() => {
    // reset feedback on takeoff, and clear any feedback queue
    if (props.flightPhase === "takeoff" && !takeoffProcessedRef.current) {
      const uavMissions = missionQueue.filter((combinedId) => {
        const [_, uavPart] = combinedId.split(":");
        return uavPart === props.uavId;
      });

      if (uavMissions.length > 0) {
        setCompletedMissionQueue((prev) => [...prev, ...uavMissions]);

        setMissionQueue((prev) =>
          prev.filter((id) => {
            const [_, uavPart] = id.split(":");
            return uavPart !== props.uavId;
          })
        );

        dispatch(clearDeliveryFeedback(props.uavId));

        setFeedbackRestored(false);
        setShowFeedback(false);
        setFeedbackText("");
        setFeedbackType("");
        setOtherFeedback("");
        setFeedbackSeverity("NO_ISSUE");

        takeoffProcessedRef.current = true;
      }
    }
  }, [props.flightPhase, props.uavId, missionQueue, dispatch]);

  useEffect(() => {
    if (props.flightPhase !== "takeoff") {
      takeoffProcessedRef.current = false;
    }
  }, [props.flightPhase]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmittingPositiveFeedback, setIsSubmittingPositiveFeedback] =
    useState<boolean>(false);

  // Auto-open negative feedback when mission completes
  useEffect(() => {
    if (savedFeedback && 
        !savedFeedback.isPositive && 
        !feedbackRestored &&
        missionId &&
        (props.flightPhase === "missionComplete" ||
         props.flightPhase === "missionReceived" ||
         props.flightPhase === "standby" ||
         props.flightPhase === "maintenance" ||
         props.flightPhase === "ready")) {
      // Restore the saved negative feedback
      setFeedbackType(savedFeedback.feedbackType);
      setOtherFeedback(savedFeedback.otherFeedback);
      setFeedbackText(savedFeedback.feedbackText);
      setFeedbackSeverity(savedFeedback.issueLevel || "MINOR_ISSUE");
      setIsSubmittingPositiveFeedback(false);
      setShowFeedback(true);
      setFeedbackRestored(true);
    }
  }, [savedFeedback, feedbackRestored, missionId, props.flightPhase]);

  const handleFeedbackClick = (isPositive: boolean) => {
    if (savedFeedback && props.flightPhase !== "climbDelivery" && !feedbackRestored) {
      if (savedFeedback.isPositive === isPositive) {
        setFeedbackType(savedFeedback.feedbackType);
        setOtherFeedback(savedFeedback.otherFeedback);
        setFeedbackText(savedFeedback.feedbackText);
        setFeedbackSeverity(savedFeedback.issueLevel || (savedFeedback.isPositive ? "NO_ISSUE" : "MINOR_ISSUE"));
        setFeedbackRestored(true);
        setIsSubmittingPositiveFeedback(isPositive);
        setShowFeedback(true);
      } else {
        if (showFeedback && isSubmittingPositiveFeedback !== isPositive) {
          setIsSubmittingPositiveFeedback(isPositive);
        } else {
          setShowFeedback((prevState) => !prevState);
          setIsSubmittingPositiveFeedback(isPositive);
        }
      }
    } else {
      if (showFeedback && isSubmittingPositiveFeedback !== isPositive) {
        setIsSubmittingPositiveFeedback(isPositive);
      } else {
        setShowFeedback((prevState) => !prevState);
        setIsSubmittingPositiveFeedback(isPositive);
      }
    }
    setFeedbackSeverity(isPositive ? "NO_ISSUE" : "MINOR_ISSUE");
  };

  const handleThumbsDownClick = () => {
    handleFeedbackClick(false);
  };

  const handleThumbsUpClick = () => {
    handleFeedbackClick(true);
  };

  const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedbackText(event.target.value);
  };

  const handleOtherFeedbackChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setOtherFeedback(value);
    if (value && feedbackSeverity === "NO_ISSUE") {
      setFeedbackSeverity("MINOR_ISSUE");
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    const isSelectMenu = !!target.closest(".MuiPopover-root");

    if (showFeedback && !isSelectMenu) {
      if (props.flightPhase === "climbDelivery") {
        if (feedbackType || otherFeedback || feedbackText) {
          dispatch(saveDeliveryFeedback({
            uavId: props.uavId,
            feedback: {
              isPositive: isSubmittingPositiveFeedback,
              feedbackType,
              otherFeedback,
              feedbackText,
              issueLevel: isSubmittingPositiveFeedback ? "NO_ISSUE" : feedbackSeverity,
            }
          }));
        }
      }
      setShowFeedback(false);
    }
  };

  const handleSubmit = async () => {
    if (!missionId) return;
    setIsLoading(true);
    try {
      let issueText = "";
      if (isSubmittingPositiveFeedback) {
        issueText = feedbackText || " ";
      } else {
        // Find the issue definition by ID
        const issueDef = ISSUE_DEFINITIONS?.[feedbackType as keyof typeof ISSUE_DEFINITIONS]
        if (feedbackType === ISSUE_DEFINITIONS.other_issue.id) {
          issueText = otherFeedback;
        } else if (issueDef) {
          issueText = issueDef.value;
        } else {
          // Fallback for legacy data that might have the value instead of ID
          issueText = feedbackType;
        }
      }

      await dispatch(
        missionReportThunk({
          uavId: props.uavId,
          missionId: missionId,
          issueLevel: isSubmittingPositiveFeedback ? "NO_ISSUE" : feedbackSeverity,
          description: issueText,
        })
      ).unwrap();

      dispatch(clearDeliveryFeedback(props.uavId));
      setFeedbackRestored(false);

      completeMissionId(missionId);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsLoading(false);
      setFeedbackText("");
      setFeedbackType("");
      setOtherFeedback("");
      setFeedbackSeverity("NO_ISSUE");
      setShowFeedback(false);
    }
  };

  const hasSavedDeliveryFeedback = () => {
    return !!savedFeedback;
  };

  if (
    (props.flightPhase === "climbDelivery" && !hasSavedDeliveryFeedback()) ||
    ((props.flightPhase === "missionComplete" ||
      props.flightPhase === "missionReceived" ||
      props.flightPhase === "standby" ||
      props.flightPhase === "maintenance" ||
      props.flightPhase === "ready") &&
      missionId)
  )
    return (
      <ClickAwayListener
        onClickAway={handleClickAway}
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        disableReactTree={true}
      >
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" component="div">
              {props.flightPhase === "climbDelivery"
                ? "Review Delivery"
                : `Review Flight ${missionId?.slice(0, 4) || ""}`}
            </Typography>

            <Box>
              {props.flightPhase === "climbDelivery" ? (
                <>
                  <IconButton
                    color="primary"
                    aria-label="thumbs up"
                    onClick={() => {
                      setIsSubmittingPositiveFeedback(true);
                      dispatch(saveDeliveryFeedback({
                        uavId: props.uavId,
                        feedback: {
                          isPositive: true,
                          feedbackType: "",
                          otherFeedback: "",
                          feedbackText: "",
                          issueLevel: "NO_ISSUE",
                        }
                      }));
                      setShowFeedback(false);
                    }}
                  >
                    <ThumbUpIcon sx={{ color: theme.palette.text.primary }} />
                  </IconButton>
                  <IconButton
                    aria-label="thumbs down"
                    onClick={handleThumbsDownClick}
                  >
                    <ThumbDownIcon sx={{ color: theme.palette.text.primary }} />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton
                    color="primary"
                    aria-label="thumbs up"
                    onClick={handleThumbsUpClick}
                  >
                    <ThumbUpIcon sx={{ color: theme.palette.text.primary }} />
                  </IconButton>
                  <IconButton
                    aria-label="thumbs down"
                    onClick={handleThumbsDownClick}
                  >
                    <ThumbDownIcon sx={{ color: theme.palette.text.primary }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>

          <Collapse in={showFeedback}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="stretch"
              width="100%"
              mt={2}
            >
              {isSubmittingPositiveFeedback ? (
                <TextField
                  inputRef={inputRef}
                  autoFocus={true}
                  label="Feedback"
                  variant="filled"
                  multiline
                  rows={2}
                  fullWidth
                  value={feedbackText}
                  onChange={handleFeedbackChange}
                  sx={{ color: "white", label: { color: "white" } }}
                />
              ) : (
                <>
                  <FormControlLabel
                    sx={{ color: "white", mb: 2, ml: 1 }}
                    control={
                      <Checkbox
                        checked={feedbackSeverity === "MAJOR_ISSUE"}
                        onChange={(e) =>
                          setFeedbackSeverity(e.target.checked ? "MAJOR_ISSUE" : "MINOR_ISSUE")
                        }
                        color="primary"
                        sx={{
                          "& .MuiSvgIcon-root": {
                            border: "1px solid rgba(255,255,255,0.9)",
                            borderRadius: "3px",
                          },
                        }}
                      />
                    }
                    label={<Typography variant="subtitle2" sx={{ color: "white" }}>Safety impact</Typography>}
                  />
                  <Typography variant="subtitle2" sx={{ color: "white", mb: 2 }}>
                    {(() => {
                      if (!feedbackType) return "Select Issue Type:";
                      const issueDef = Object.values(ISSUE_DEFINITIONS).find(def => def.id === feedbackType);
                      if (issueDef) return issueDef.value;
                      // Fallback for legacy data
                      return feedbackType;
                    })()}
                  </Typography>
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    {Object.entries(ISSUE_DEFINITIONS).map(([key, issue]) => {
                      const IconComponent = issue.icon;
                      return (
                        <Grid item xs={3} key={issue.id}>
                          <Paper
                            elevation={feedbackType === issue.id ? 4 : 1}
                            sx={{
                              p: 2,
                              textAlign: "center",
                              cursor: "pointer",
                              backgroundColor: feedbackType === issue.id 
                                ? theme.palette.primary.main 
                                : theme.palette.background.paper,
                              color: feedbackType === issue.id 
                                ? theme.palette.primary.contrastText 
                                : theme.palette.text.primary,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                backgroundColor: feedbackType === issue.id 
                                  ? theme.palette.primary.dark 
                                  : theme.palette.action.hover,
                              },
                            }}
                            onClick={() => {
                              setFeedbackType(issue.id);
                              if (issue.id !== ISSUE_DEFINITIONS.other_issue.id) {
                                setOtherFeedback("");
                              }
                            }}
                            aria-label={issue.value}
                          >
                            <IconComponent />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {feedbackType === ISSUE_DEFINITIONS.other_issue.id && (
                    <TextField
                      label="Specify Issue"
                      variant="filled"
                      multiline
                      rows={2}
                      fullWidth
                      value={otherFeedback}
                      onChange={handleOtherFeedbackChange}
                      sx={{
                        color: "white",
                        label: { color: "white" },
                        mt: 1,
                      }}
                    />
                  )}
                </>
              )}
              {props.flightPhase === "climbDelivery" ? (
                <Button
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    const feedbackData = {
                      isPositive: isSubmittingPositiveFeedback,
                      feedbackType,
                      otherFeedback,
                      feedbackText,
                      issueLevel: isSubmittingPositiveFeedback ? "NO_ISSUE" : feedbackSeverity,
                    };

                    dispatch(saveDeliveryFeedback({
                      uavId: props.uavId,
                      feedback: feedbackData
                    }));

                    setShowFeedback(false);
                  }}
                  disabled={
                    !isSubmittingPositiveFeedback &&
                    (!feedbackType ||
                      (feedbackType === ISSUE_DEFINITIONS.other_issue.id && !otherFeedback))
                  }
                >
                  Save Feedback
                </Button>
              ) : (
                <Button
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit}
                  disabled={
                    isLoading ||
                    (!isSubmittingPositiveFeedback &&
                      (!feedbackType ||
                        (feedbackType === ISSUE_DEFINITIONS.other_issue.id && !otherFeedback)))
                  }
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" sx={{}} />
                  ) : (
                    "Submit"
                  )}
                </Button>
              )}
              {props.flightPhase === "climbDelivery" ? (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, fontStyle: "italic", color: "gray" }}
                >
                  Submission available after flight
                </Typography>
              ) : null}
            </Box>
          </Collapse>
        </Box>
      </ClickAwayListener>
    );

  return null;
};
